import {escapeHtml as escapePrintValue} from "../../utils/escapeHtml";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  formatMoney,
  roundMoney,
  toAmount,
} from "../../services/billingService";

import {
  getBusinessDateKey,
} from "../../utils/businessDate";

import {
  logExpenseCreated,
} from "../../services/auditLogService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const normalizePaymentMode = (value) => {
  const mode = lower(value);

  if (mode === "cash") return "Cash";
  if (mode === "upi") return "UPI";
  if (mode === "card") return "Card";

  if (
    mode.includes("bank") ||
    mode.includes("transfer")
  ) {
    return "Bank Transfer";
  }

  return clean(value) || "Other";
};

const getDateObject = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(
      value.seconds * 1000
    );
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const getTimestamp = (value) =>
  getDateObject(value)?.getTime() || 0;

const formatDateTime = (value) => {
  const date = getDateObject(value);

  if (!date) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTodayInputValue = () => {
  const date = new Date();

  return [
    date.getFullYear(),
    String(
      date.getMonth() + 1
    ).padStart(2, "0"),
    String(
      date.getDate()
    ).padStart(2, "0"),
  ].join("-");
};

const createExpenseForm = () => ({
  title: "",
  category: "Miscellaneous",
  amount: "",
  paymentMode: "Cash",
  transactionReference: "",
  vendorName: "",
  billNumber: "",
  expenseDate: getTodayInputValue(),
  note: "",
});

const EXPENSE_CATEGORIES = [
  "Kitchen",
  "Housekeeping",
  "Maintenance",
  "Electricity",
  "Water",
  "Internet",
  "Salary",
  "Staff Food",
  "Laundry",
  "Marketing",
  "OTA Commission",
  "Transport",
  "Fuel",
  "Office Supplies",
  "Guest Refund",
  "Petty Cash",
  "Miscellaneous",
];

const PAYMENT_MODES = [
  "Cash",
  "UPI",
  "Card",
  "Bank Transfer",
  "Other",
];

/* -------------------------------------------------------------------------- */
/*                              Reusable Field                                */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  required = false,
  error = "",
  children,
}) {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>
        {label}

        {required && (
          <span style={requiredStyle}>
            {" "}
            *
          </span>
        )}
      </span>

      {children}

      {error && (
        <span style={fieldErrorStyle}>
          {error}
        </span>
      )}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Expense Panel                                */
/* -------------------------------------------------------------------------- */

function ExpensePanel({
  hotelId,
  hotelName,
}) {
  const [
    businessDate,
    setBusinessDate,
  ] = useState(
    getBusinessDateKey(new Date())
  );

  const [expenses, setExpenses] =
    useState([]);

  const [form, setForm] =
    useState(
      createExpenseForm()
    );

  const [errors, setErrors] =
    useState({});

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [loadError, setLoadError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [
    categoryFilter,
    setCategoryFilter,
  ] = useState("all");

  const [
    paymentFilter,
    setPaymentFilter,
  ] = useState("all");

  const [sortBy, setSortBy] =
    useState("recent");

  /* ------------------------------------------------------------------------ */
  /*                           Firestore Listener                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId || !businessDate) {
      setExpenses([]);
      setLoading(false);

      return undefined;
    }

    setLoading(true);
    setLoadError("");

    const expensesQuery = query(
      collection(
        db,
        "hotelExpenses"
      ),

      where(
        "hotelId",
        "==",
        hotelId
      ),

      where(
        "businessDate",
        "==",
        businessDate
      )
    );

    return onSnapshot(
      expensesQuery,

      (snapshot) => {
        const list =
          snapshot.docs
            .map(
              (expenseDocument) => ({
                id:
                  expenseDocument.id,

                ...expenseDocument.data(),
              })
            )
            .sort(
              (
                firstExpense,
                secondExpense
              ) =>
                getTimestamp(
                  secondExpense.expenseAt ||
                    secondExpense.createdAt
                ) -
                getTimestamp(
                  firstExpense.expenseAt ||
                    firstExpense.createdAt
                )
            );

        setExpenses(list);
        setLoading(false);
        setLoadError("");
      },

      (error) => {
        console.error(
          "Expense load error:",
          error
        );

        setExpenses([]);
        setLoading(false);

        setLoadError(
          error?.message ||
            "Expenses load nahi hue."
        );
      }
    );
  }, [
    hotelId,
    businessDate,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                               Totals                                      */
  /* ------------------------------------------------------------------------ */

  const totals = useMemo(() => {
    const result = {
      totalExpense: 0,
      cashExpense: 0,
      onlineExpense: 0,
      upiExpense: 0,
      cardExpense: 0,
      bankExpense: 0,
      otherExpense: 0,
      count: expenses.length,
    };

    expenses.forEach((expense) => {
      const amount = Math.max(
        0,
        toAmount(expense.amount)
      );

      const mode =
        normalizePaymentMode(
          expense.paymentMode
        );

      result.totalExpense += amount;

      if (mode === "Cash") {
        result.cashExpense += amount;
      } else {
        result.onlineExpense += amount;

        if (mode === "UPI") {
          result.upiExpense += amount;
        } else if (mode === "Card") {
          result.cardExpense += amount;
        } else if (
          mode === "Bank Transfer"
        ) {
          result.bankExpense += amount;
        } else {
          result.otherExpense += amount;
        }
      }
    });

    Object.keys(result).forEach(
      (key) => {
        if (
          typeof result[key] ===
          "number"
        ) {
          result[key] = roundMoney(
            result[key]
          );
        }
      }
    );

    return result;
  }, [expenses]);

  /* ------------------------------------------------------------------------ */
  /*                          Filter and Sorting                               */
  /* ------------------------------------------------------------------------ */

  const filteredExpenses =
    useMemo(() => {
      const searchTerm =
        lower(search);

      const filtered =
        expenses.filter((expense) => {
          const expenseText = [
            expense.title,
            expense.category,
            expense.vendorName,
            expense.billNumber,
            expense.transactionReference,
            expense.note,
            expense.paymentMode,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          const matchesSearch =
            !searchTerm ||
            expenseText.includes(
              searchTerm
            );

          const matchesCategory =
            categoryFilter === "all" ||
            expense.category ===
              categoryFilter;

          const matchesPayment =
            paymentFilter === "all" ||
            normalizePaymentMode(
              expense.paymentMode
            ) === paymentFilter;

          return (
            matchesSearch &&
            matchesCategory &&
            matchesPayment
          );
        });

      return [...filtered].sort(
        (
          firstExpense,
          secondExpense
        ) => {
          if (sortBy === "amount_high") {
            return (
              toAmount(
                secondExpense.amount
              ) -
              toAmount(
                firstExpense.amount
              )
            );
          }

          if (sortBy === "amount_low") {
            return (
              toAmount(
                firstExpense.amount
              ) -
              toAmount(
                secondExpense.amount
              )
            );
          }

          if (sortBy === "category") {
            return clean(
              firstExpense.category
            ).localeCompare(
              clean(
                secondExpense.category
              )
            );
          }

          if (sortBy === "oldest") {
            return (
              getTimestamp(
                firstExpense.expenseAt ||
                  firstExpense.createdAt
              ) -
              getTimestamp(
                secondExpense.expenseAt ||
                  secondExpense.createdAt
              )
            );
          }

          return (
            getTimestamp(
              secondExpense.expenseAt ||
                secondExpense.createdAt
            ) -
            getTimestamp(
              firstExpense.expenseAt ||
                firstExpense.createdAt
            )
          );
        }
      );
    }, [
      expenses,
      search,
      categoryFilter,
      paymentFilter,
      sortBy,
    ]);

  /* ------------------------------------------------------------------------ */
  /*                             Form Updater                                  */
  /* ------------------------------------------------------------------------ */

  const updateForm = (
    field,
    value
  ) => {
    setForm((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      if (
        field === "paymentMode" &&
        value === "Cash"
      ) {
        next.transactionReference = "";
      }

      return next;
    });

    setErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = {
        ...current,
      };

      delete next[field];

      return next;
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                              Validation                                   */
  /* ------------------------------------------------------------------------ */

  const validate = () => {
    const nextErrors = {};

    if (!clean(form.title)) {
      nextErrors.title =
        "Expense ka naam required hai.";
    }

    if (
      toAmount(form.amount) <= 0
    ) {
      nextErrors.amount =
        "Valid expense amount required hai.";
    }

    if (!clean(form.category)) {
      nextErrors.category =
        "Expense category required hai.";
    }

    if (!clean(form.paymentMode)) {
      nextErrors.paymentMode =
        "Payment mode required hai.";
    }

    if (
      form.paymentMode !== "Cash" &&
      !clean(
        form.transactionReference
      )
    ) {
      nextErrors.transactionReference =
        "Online payment ka transaction reference required hai.";
    }

    if (!form.expenseDate) {
      nextErrors.expenseDate =
        "Expense date required hai.";
    }

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors)
        .length === 0
    );
  };

  /* ------------------------------------------------------------------------ */
  /*                              Add Expense                                  */
  /* ------------------------------------------------------------------------ */

  const submitExpense = async (
    event
  ) => {
    event.preventDefault();

    if (
      saving ||
      !validate()
    ) {
      return;
    }

    const amount = roundMoney(
      Math.max(
        0,
        toAmount(form.amount)
      )
    );

    const expenseDate =
      new Date(
        `${form.expenseDate}T12:00:00+05:30`
      );

    if (
      Number.isNaN(
        expenseDate.getTime()
      )
    ) {
      alert(
        "Expense date invalid hai."
      );

      return;
    }

    setSaving(true);

    try {
      const now = new Date();

      /*
       * Phase 2 financial integrity:
       * Expense master record + Day Book entry ek hi atomic batch me save hote hain.
       * Agar ek write fail ho, dono fail honge; half-saved expense nahi banega.
       */
      const operationId =
        `EXP_${hotelId}_${now.getTime()}_${Math.random()
          .toString(36)
          .slice(2, 8)}`;

      const expenseReference =
        doc(
          collection(
            db,
            "hotelExpenses"
          )
        );

      const dayBookReference =
        doc(
          db,
          "dayBookEntries",
          `${operationId}_DAYBOOK`
        );

      const expensePayload = {
        hotelId,

        hotelName:
          clean(hotelName),

        businessDate: getBusinessDateKey(expenseDate),

        title:
          clean(form.title),

        category:
          clean(form.category),

        amount,

        paymentMode:
          normalizePaymentMode(
            form.paymentMode
          ),

        transactionReference:
          clean(
            form.transactionReference
          ),

        vendorName:
          clean(form.vendorName),

        billNumber:
          clean(form.billNumber),

        expenseDate:
          form.expenseDate,

        expenseAt:
          expenseDate.toISOString(),

        note:
          clean(form.note),

        source:
          "expense_panel",

        operationId,

        createdAt:
          serverTimestamp(),

        updatedAt:
          serverTimestamp(),
      };

      const dayBookPayload = {
        hotelId,

        hotelName:
          clean(hotelName),

        businessDate: getBusinessDateKey(expenseDate),

        entryType: "expense",

        category:
          clean(form.category),

        title:
          clean(form.title),

        amount,

        paymentMode:
          normalizePaymentMode(
            form.paymentMode
          ),

        transactionReference:
          clean(
            form.transactionReference
          ),

        vendorName:
          clean(form.vendorName),

        billNumber:
          clean(form.billNumber),

        expenseId:
          expenseReference.id,

        operationId,

        note:
          clean(form.note),

        eventAt:
          expenseDate.toISOString(),

        source:
          "expense_panel",

        createdAt:
          serverTimestamp(),

        updatedAt:
          serverTimestamp(),
      };

      const batch =
        writeBatch(db);

      batch.set(
        expenseReference,
        expensePayload
      );

      batch.set(
        dayBookReference,
        dayBookPayload
      );

      await batch.commit();

      setForm(
        createExpenseForm()
      );

      setErrors({});

      try {
        await logExpenseCreated({
          hotelId, hotelName: clean(hotelName), amount, title: clean(form.title),
          description: `${clean(form.category)} expense • ${normalizePaymentMode(form.paymentMode)}`,
          referenceId: clean(form.transactionReference) || clean(form.billNumber) || `${businessDate}-${Date.now()}`,
          metadata: { category: clean(form.category), paymentMode: normalizePaymentMode(form.paymentMode), transactionReference: clean(form.transactionReference), vendorName: clean(form.vendorName), billNumber: clean(form.billNumber), note: clean(form.note) },
          eventAt: expenseDate,
        });
      } catch (auditError) { console.warn("Expense audit log error:", auditError); }

      alert(
        "Expense save ho gaya ✅"
      );
    } catch (error) {
      console.error(
        "Expense save error:",
        error
      );

      alert(
        "Expense save nahi hua: " +
          (error?.message || error)
      );
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                                 Print                                     */
  /* ------------------------------------------------------------------------ */

  const printExpenses = () => {
    const printWindow =
      window.open(
        "",
        "_blank",
        "width=1100,height=850"
      );

    if (!printWindow) {
      alert(
        "Print popup block ho gaya. Browser me popups allow karo."
      );

      return;
    }

    const rows =
      filteredExpenses
        .map(
          (expense, index) => `
            <tr>
              <td>${index + 1}</td>

              <td>
                ${escapePrintValue(clean(
                  formatDateTime(
                    expense.expenseAt ||
                      expense.createdAt
                  )
                ))}
              </td>

              <td>
                ${escapePrintValue(clean(
                  expense.category || "-"
                ))}
              </td>

              <td>
                ${escapePrintValue(clean(
                  expense.title || "-"
                ))}
              </td>

              <td>
                ${escapePrintValue(clean(
                  expense.vendorName || "-"
                ))}
              </td>

              <td>
                ${escapePrintValue(clean(
                  expense.paymentMode || "-"
                ))}
              </td>

              <td>
                ${escapePrintValue(clean(
                  expense.transactionReference ||
                    "-"
                ))}
              </td>

              <td>
                ${escapePrintValue(clean(
                  expense.note || "-"
                ))}
              </td>

              <td class="right">
                ${formatMoney(
                  expense.amount
                )}
              </td>
            </tr>
          `
        )
        .join("");

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>

      <html>
        <head>
          <meta charset="UTF-8" />

          <title>
            Expense Register ${businessDate}
          </title>

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 10mm;
              color: #0f172a;
              font-family:
                Arial,
                sans-serif;
            }

            h1 {
              margin: 0;
              text-align: center;
              font-size: 21px;
            }

            .subtitle {
              margin-top: 5px;
              text-align: center;
              color: #475569;
              font-size: 10px;
            }

            .summary {
              display: grid;
              grid-template-columns:
                repeat(4,1fr);
              gap: 7px;
              margin: 14px 0;
            }

            .summary-box {
              padding: 8px;
              border: 1px solid #94a3b8;
              text-align: center;
              font-size: 9px;
            }

            .summary-box strong {
              display: block;
              margin-top: 4px;
              font-size: 13px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th,
            td {
              padding: 6px;
              border: 1px solid #94a3b8;
              font-size: 8px;
              vertical-align: top;
            }

            th {
              background: #e2e8f0;
              text-align: left;
            }

            .right {
              text-align: right;
              font-weight: 900;
            }

            @page {
              size: A4 landscape;
              margin: 7mm;
            }
          </style>
        </head>

        <body>
          <h1>
            ${escapePrintValue(clean(
              hotelName || hotelId
            ))} — EXPENSE REGISTER
          </h1>

          <div class="subtitle">
            Business Date:
            ${businessDate}
          </div>

          <div class="summary">
            <div class="summary-box">
              Total Expenses
              <strong>
                ${formatMoney(
                  totals.totalExpense
                )}
              </strong>
            </div>

            <div class="summary-box">
              Cash
              <strong>
                ${formatMoney(
                  totals.cashExpense
                )}
              </strong>
            </div>

            <div class="summary-box">
              Online
              <strong>
                ${formatMoney(
                  totals.onlineExpense
                )}
              </strong>
            </div>

            <div class="summary-box">
              Entries
              <strong>
                ${totals.count}
              </strong>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Date & Time</th>
                <th>Category</th>
                <th>Description</th>
                <th>Vendor</th>
                <th>Mode</th>
                <th>Transaction</th>
                <th>Note</th>
                <th>Amount</th>
              </tr>
            </thead>

            <tbody>
              ${rows}
            </tbody>
          </table>

          <script>
            window.onload = function () {
              setTimeout(function () {
                window.focus();
                window.print();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <section style={sectionStyle}>
      <header style={headerStyle}>
        <div>
          <h3 style={titleStyle}>
            💸 Hotel Expenses
          </h3>

          <div style={subtitleStyle}>
            Daily expenses, cash flow aur
            online payment tracking
          </div>
        </div>

        <div style={headerActionsStyle}>
          <input
            type="date"
            value={businessDate}
            onChange={(event) =>
              setBusinessDate(
                event.target.value
              )
            }
            style={inputStyle}
          />

          <button
            type="button"
            onClick={printExpenses}
            style={printButtonStyle}
          >
            🖨 Print Expenses
          </button>
        </div>
      </header>

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Total Expenses"
          value={formatMoney(
            totals.totalExpense
          )}
          danger={
            totals.totalExpense > 0
          }
        />

        <SummaryCard
          label="Cash Expense"
          value={formatMoney(
            totals.cashExpense
          )}
        />

        <SummaryCard
          label="Online Expense"
          value={formatMoney(
            totals.onlineExpense
          )}
        />

        <SummaryCard
          label="UPI"
          value={formatMoney(
            totals.upiExpense
          )}
        />

        <SummaryCard
          label="Card"
          value={formatMoney(
            totals.cardExpense
          )}
        />

        <SummaryCard
          label="Bank"
          value={formatMoney(
            totals.bankExpense
          )}
        />

        <SummaryCard
          label="Entries"
          value={totals.count}
        />
      </div>

      <form
        onSubmit={submitExpense}
        style={formSectionStyle}
      >
        <h4 style={sectionTitleStyle}>
          Add New Expense
        </h4>

        <div style={formGridStyle}>
          <Field
            label="Expense Name"
            required
            error={errors.title}
          >
            <input
              value={form.title}
              disabled={saving}
              onChange={(event) =>
                updateForm(
                  "title",
                  event.target.value
                )
              }
              style={{
                ...inputStyle,
                ...getErrorStyle(
                  errors.title
                ),
              }}
              placeholder="Example: Kitchen purchase"
            />
          </Field>

          <Field
            label="Category"
            required
            error={errors.category}
          >
            <select
              value={form.category}
              disabled={saving}
              onChange={(event) =>
                updateForm(
                  "category",
                  event.target.value
                )
              }
              style={{
                ...inputStyle,
                ...getErrorStyle(
                  errors.category
                ),
              }}
            >
              {EXPENSE_CATEGORIES.map(
                (category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {category}
                  </option>
                )
              )}
            </select>
          </Field>

          <Field
            label="Amount"
            required
            error={errors.amount}
          >
            <input
              type="number"
              min="0"
              value={form.amount}
              disabled={saving}
              onChange={(event) =>
                updateForm(
                  "amount",
                  event.target.value
                )
              }
              style={{
                ...inputStyle,
                ...getErrorStyle(
                  errors.amount
                ),
              }}
              placeholder="₹"
            />
          </Field>

          <Field
            label="Payment Mode"
            required
            error={errors.paymentMode}
          >
            <select
              value={form.paymentMode}
              disabled={saving}
              onChange={(event) =>
                updateForm(
                  "paymentMode",
                  event.target.value
                )
              }
              style={{
                ...inputStyle,
                ...getErrorStyle(
                  errors.paymentMode
                ),
              }}
            >
              {PAYMENT_MODES.map(
                (mode) => (
                  <option
                    key={mode}
                    value={mode}
                  >
                    {mode}
                  </option>
                )
              )}
            </select>
          </Field>

          {form.paymentMode !==
            "Cash" && (
            <Field
              label="Transaction Reference"
              required
              error={
                errors.transactionReference
              }
            >
              <input
                value={
                  form.transactionReference
                }
                disabled={saving}
                onChange={(event) =>
                  updateForm(
                    "transactionReference",
                    event.target.value
                  )
                }
                style={{
                  ...inputStyle,
                  ...getErrorStyle(
                    errors.transactionReference
                  ),
                }}
                placeholder="UPI / Card / Bank reference"
              />
            </Field>
          )}

          <Field label="Vendor Name">
            <input
              value={form.vendorName}
              disabled={saving}
              onChange={(event) =>
                updateForm(
                  "vendorName",
                  event.target.value
                )
              }
              style={inputStyle}
              placeholder="Shop / supplier name"
            />
          </Field>

          <Field label="Bill Number">
            <input
              value={form.billNumber}
              disabled={saving}
              onChange={(event) =>
                updateForm(
                  "billNumber",
                  event.target.value
                )
              }
              style={inputStyle}
              placeholder="Invoice / bill number"
            />
          </Field>

          <Field
            label="Expense Date"
            required
            error={errors.expenseDate}
          >
            <input
              type="date"
              value={form.expenseDate}
              disabled={saving}
              onChange={(event) =>
                updateForm(
                  "expenseDate",
                  event.target.value
                )
              }
              style={{
                ...inputStyle,
                ...getErrorStyle(
                  errors.expenseDate
                ),
              }}
            />
          </Field>

          <Field label="Note">
            <input
              value={form.note}
              disabled={saving}
              onChange={(event) =>
                updateForm(
                  "note",
                  event.target.value
                )
              }
              style={inputStyle}
              placeholder="Optional expense details"
            />
          </Field>
        </div>

        <button
          disabled={saving}
          style={{
            ...saveButtonStyle,
            opacity: saving
              ? 0.6
              : 1,
          }}
        >
          {saving
            ? "Saving Expense..."
            : `Save Expense • ${formatMoney(
                form.amount
              )}`}
        </button>
      </form>

      <section style={listSectionStyle}>
        <div style={listHeaderStyle}>
          <div>
            <h4 style={sectionTitleStyle}>
              Expense Register
            </h4>

            <div style={subtitleStyle}>
              {filteredExpenses.length} of{" "}
              {expenses.length} expenses
            </div>
          </div>

          <div style={filterGridStyle}>
            <input
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              style={inputStyle}
              placeholder="Expense, vendor, bill, transaction..."
            />

            <select
              value={categoryFilter}
              onChange={(event) =>
                setCategoryFilter(
                  event.target.value
                )
              }
              style={inputStyle}
            >
              <option value="all">
                All Categories
              </option>

              {EXPENSE_CATEGORIES.map(
                (category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {category}
                  </option>
                )
              )}
            </select>

            <select
              value={paymentFilter}
              onChange={(event) =>
                setPaymentFilter(
                  event.target.value
                )
              }
              style={inputStyle}
            >
              <option value="all">
                All Payment Modes
              </option>

              {PAYMENT_MODES.map(
                (mode) => (
                  <option
                    key={mode}
                    value={mode}
                  >
                    {mode}
                  </option>
                )
              )}
            </select>

            <select
              value={sortBy}
              onChange={(event) =>
                setSortBy(
                  event.target.value
                )
              }
              style={inputStyle}
            >
              <option value="recent">
                Recent First
              </option>

              <option value="oldest">
                Oldest First
              </option>

              <option value="amount_high">
                Highest Amount
              </option>

              <option value="amount_low">
                Lowest Amount
              </option>

              <option value="category">
                Category
              </option>
            </select>
          </div>
        </div>

        {loadError && (
          <div style={errorBoxStyle}>
            {loadError}
          </div>
        )}

        {loading ? (
          <div style={emptyBoxStyle}>
            Expenses loading...
          </div>
        ) : filteredExpenses.length ===
          0 ? (
          <div style={emptyBoxStyle}>
            Matching expense nahi mila.
          </div>
        ) : (
          <div style={tableWrapperStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>
                    Date
                  </th>

                  <th style={thStyle}>
                    Category
                  </th>

                  <th style={thStyle}>
                    Expense
                  </th>

                  <th style={thStyle}>
                    Vendor / Bill
                  </th>

                  <th style={thStyle}>
                    Payment
                  </th>

                  <th style={thStyle}>
                    Reference
                  </th>

                  <th style={thStyle}>
                    Note
                  </th>

                  <th style={thRightStyle}>
                    Amount
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredExpenses.map(
                  (expense) => (
                    <tr key={expense.id}>
                      <td style={tdStyle}>
                        {formatDateTime(
                          expense.expenseAt ||
                            expense.createdAt
                        )}
                      </td>

                      <td style={tdStyle}>
                        <CategoryBadge
                          category={
                            expense.category
                          }
                        />
                      </td>

                      <td style={tdStyle}>
                        <strong>
                          {expense.title ||
                            "-"}
                        </strong>
                      </td>

                      <td style={tdStyle}>
                        {expense.vendorName ||
                          "-"}

                        {expense.billNumber && (
                          <div style={subTextStyle}>
                            Bill:{" "}
                            {expense.billNumber}
                          </div>
                        )}
                      </td>

                      <td style={tdStyle}>
                        {expense.paymentMode ||
                          "-"}
                      </td>

                      <td
                        style={{
                          ...tdStyle,

                          background:
                            expense.paymentMode !==
                              "Cash" &&
                            expense.transactionReference
                              ? "#fef08a"
                              : "transparent",

                          fontWeight:
                            expense.transactionReference
                              ? 800
                              : 500,
                        }}
                      >
                        {expense.transactionReference ||
                          "-"}
                      </td>

                      <td style={tdStyle}>
                        {expense.note ||
                          "-"}
                      </td>

                      <td style={tdRightStyle}>
                        <strong
                          style={{
                            color: "#dc2626",
                          }}
                        >
                          -
                          {formatMoney(
                            expense.amount
                          )}
                        </strong>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function SummaryCard({
  label,
  value,
  danger = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <span style={summaryLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...summaryValueStyle,

          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function CategoryBadge({
  category,
}) {
  return (
    <span style={categoryBadgeStyle}>
      {category || "Miscellaneous"}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const getErrorStyle = (error) =>
  error
    ? {
        borderColor: "#dc2626",
        boxShadow:
          "0 0 0 2px rgba(220,38,38,.08)",
      }
    : {};

const sectionStyle = {
  marginTop: 34,
  padding: 18,

  border:
    "1px solid #e2e8f0",

  borderRadius: 18,

  background: "#ffffff",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 11,
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(125px,1fr))",

  gap: 9,

  marginTop: 15,
};

const summaryCardStyle = {
  padding: 11,

  borderRadius: 11,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",
};

const summaryLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  display: "block",

  marginTop: 4,

  fontSize: 17,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const formSectionStyle = {
  marginTop: 15,
  padding: 14,

  borderRadius: 14,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",
};

const sectionTitleStyle = {
  margin: "0 0 11px",

  color: "#0f172a",

  fontSize: 15,
};

const formGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(190px,1fr))",

  gap: 10,
};

const fieldStyle = {
  display: "block",
};

const fieldLabelStyle = {
  display: "block",

  marginBottom: 6,

  color: "#334155",

  fontSize: 11,
  fontWeight: 800,
};

const requiredStyle = {
  color: "#dc2626",
};

const fieldErrorStyle = {
  display: "block",

  marginTop: 5,

  color: "#dc2626",

  fontSize: 9,
  fontWeight: 700,
};

const inputStyle = {
  width: "100%",
  minHeight: 42,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 9,

  background: "#ffffff",
  color: "#0f172a",

  boxSizing: "border-box",

  outline: "none",
};

const saveButtonStyle = {
  width: "100%",

  marginTop: 11,
  padding: 11,

  border: 0,
  borderRadius: 10,

  background: "#dc2626",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const printButtonStyle = {
  minHeight: 42,

  padding: "9px 13px",

  border: 0,
  borderRadius: 9,

  background: "#0f766e",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const listSectionStyle = {
  marginTop: 15,
};

const listHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,
  flexWrap: "wrap",
};

const filterGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "minmax(210px,1fr) repeat(3,minmax(140px,180px))",

  gap: 8,

  flex: 1,
  minWidth: 300,
};

const errorBoxStyle = {
  marginTop: 12,
  padding: 11,

  border:
    "1px solid #fecaca",

  borderRadius: 10,

  background: "#fef2f2",
  color: "#b91c1c",

  fontSize: 11,
  fontWeight: 700,
};

const emptyBoxStyle = {
  marginTop: 12,
  padding: 17,

  border:
    "1px dashed #cbd5e1",

  borderRadius: 10,

  color: "#64748b",

  textAlign: "center",
};

const tableWrapperStyle = {
  marginTop: 12,

  overflowX: "auto",

  border:
    "1px solid #cbd5e1",

  borderRadius: 10,
};

const tableStyle = {
  width: "100%",

  minWidth: 1000,

  borderCollapse: "collapse",

  background: "#ffffff",
};

const thStyle = {
  padding: 9,

  borderBottom:
    "1px solid #cbd5e1",

  background: "#e2e8f0",

  color: "#334155",

  fontSize: 9,
  fontWeight: 900,

  textAlign: "left",

  whiteSpace: "nowrap",
};

const thRightStyle = {
  ...thStyle,
  textAlign: "right",
};

const tdStyle = {
  padding: 9,

  borderBottom:
    "1px solid #e2e8f0",

  color: "#334155",

  fontSize: 10,

  verticalAlign: "top",
};

const tdRightStyle = {
  ...tdStyle,
  textAlign: "right",
};

const categoryBadgeStyle = {
  display: "inline-block",

  padding: "4px 7px",

  borderRadius: 999,

  background: "#f3e8ff",
  color: "#7c3aed",

  fontSize: 9,
  fontWeight: 900,

  whiteSpace: "nowrap",
};

const subTextStyle = {
  marginTop: 3,

  color: "#64748b",

  fontSize: 8,
};

export default ExpensePanel;